import { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Upload, X, FileText, Loader2 } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/use-toast';
import { ModuleTag, Needle1DocumentType, type CriminalCase } from '@/types';

interface DocumentUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  prisonerId: string;
  criminalCases?: CriminalCase[];
}

const moduleLabels: Record<ModuleTag, string> = {
  [ModuleTag.VOLLZUGSVERLAUF]: 'Vollzugsverlauf',
  [ModuleTag.VOLLZUGSPLANUNG]: 'Vollzugsplanung',
  [ModuleTag.DISZIPLINAR]: 'Disziplinarmaßnahmen',
  [ModuleTag.LOCKERUNGEN]: 'Lockerungen/Ausgänge',
  [ModuleTag.BESUCHE]: 'Besuche/Kontakte',
  [ModuleTag.DOKUMENTE]: 'Dokumente',
  [ModuleTag.NOTIZEN]: 'Notizen/Vermerke',
  [ModuleTag.VOLLSTRECKUNG]: 'Vollstreckung',
};

const needle1DocTypeLabels: Record<Needle1DocumentType, string> = {
  [Needle1DocumentType.AUFNAHMESCHEIN]: 'Aufnahmeschein',
  [Needle1DocumentType.PERSONALBOGEN]: 'Personalbogen',
  [Needle1DocumentType.LICHTBILD]: 'Lichtbild',
  [Needle1DocumentType.FINGERABDRUCK]: 'Fingerabdruckbogen',
  [Needle1DocumentType.VOLLZUGSPLAN]: 'Vollzugsplan',
  [Needle1DocumentType.ENTLASSUNGSPAPIERE]: 'Entlassungspapiere',
  [Needle1DocumentType.VERLEGUNGSURKUNDE]: 'Verlegungsurkunde',
};

const acceptedFileTypes = {
  'application/pdf': ['.pdf'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'application/vnd.ms-excel': ['.xls'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'text/csv': ['.csv'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
};

export function DocumentUploadDialog({
  open,
  onOpenChange,
  prisonerId,
  criminalCases = [],
}: DocumentUploadDialogProps) {
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [needle, setNeedle] = useState<'1' | '2' | '3'>('3');
  const [needle1DocType, setNeedle1DocType] = useState<Needle1DocumentType | ''>('');
  const [caseId, setCaseId] = useState<string>('');
  const [selectedModules, setSelectedModules] = useState<ModuleTag[]>([ModuleTag.DOKUMENTE]);
  const [comment, setComment] = useState('');

  const { toast } = useToast();
  const queryClient = useQueryClient();

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      const uploadedFile = acceptedFiles[0];
      setFile(uploadedFile);
      if (!title) {
        // Use filename without extension as default title
        setTitle(uploadedFile.name.replace(/\.[^/.]+$/, ''));
      }
    }
  }, [title]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: acceptedFileTypes,
    maxFiles: 1,
    maxSize: 50 * 1024 * 1024, // 50MB
  });

  const uploadMutation = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error('Keine Datei ausgewählt');

      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', title);
      formData.append('needle', needle);
      formData.append('modules', JSON.stringify(selectedModules));
      formData.append('dispositionDate', new Date().toISOString());
      
      if (needle === '1' && needle1DocType) {
        formData.append('needle1DocType', needle1DocType);
      }
      if (needle === '2' && caseId) {
        formData.append('caseId', caseId);
      }
      if (comment) {
        formData.append('comment', comment);
      }

      const token = localStorage.getItem('token');
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

      // First create the record item
      const recordResponse = await fetch(`${API_URL}/api/v1/prisoners/${prisonerId}/records`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          type: 'DOCUMENT',
          title,
          modules: selectedModules,
          needle: parseInt(needle),
          needle1DocType: needle === '1' ? needle1DocType : undefined,
          caseId: needle === '2' ? caseId : undefined,
          dispositionDate: new Date().toISOString(),
          dispositionComment: comment || undefined,
        }),
      });

      if (!recordResponse.ok) {
        const error = await recordResponse.json();
        throw new Error(error.message || 'Fehler beim Erstellen des Akteneintrags');
      }

      const record = await recordResponse.json();

      // Then upload the document
      const docFormData = new FormData();
      docFormData.append('file', file);
      docFormData.append('dispositionDate', new Date().toISOString());
      if (comment) {
        docFormData.append('comment', comment);
      }

      const uploadResponse = await fetch(
        `${API_URL}/api/v1/records/${record.id}/documents`,
        {
          method: 'POST',
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: docFormData,
        }
      );

      if (!uploadResponse.ok) {
        const error = await uploadResponse.json();
        throw new Error(error.message || 'Fehler beim Hochladen der Datei');
      }

      return uploadResponse.json();
    },
    onSuccess: () => {
      toast({
        title: 'Dokument hochgeladen',
        description: `"${title}" wurde erfolgreich zur Akte hinzugefügt.`,
      });
      queryClient.invalidateQueries({ queryKey: ['timeline', prisonerId] });
      queryClient.invalidateQueries({ queryKey: ['records', prisonerId] });
      queryClient.invalidateQueries({ queryKey: ['needle-view', prisonerId] });
      handleClose();
    },
    onError: (error: Error) => {
      toast({
        title: 'Fehler beim Upload',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const handleClose = () => {
    setFile(null);
    setTitle('');
    setNeedle('3');
    setNeedle1DocType('');
    setCaseId('');
    setSelectedModules([ModuleTag.DOKUMENTE]);
    setComment('');
    onOpenChange(false);
  };

  const toggleModule = (module: ModuleTag) => {
    setSelectedModules((prev) =>
      prev.includes(module)
        ? prev.filter((m) => m !== module)
        : [...prev, module]
    );
  };

  const canSubmit = file && title && selectedModules.length > 0 &&
    (needle !== '1' || needle1DocType) &&
    (needle !== '2' || caseId);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Dokument hochladen</DialogTitle>
          <DialogDescription>
            Laden Sie ein Dokument zur Gefangenenakte hoch und ordnen Sie es einer Nadel zu.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* File Upload Zone */}
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
              isDragActive
                ? 'border-primary bg-primary/5'
                : file
                  ? 'border-green-500 bg-green-50'
                  : 'border-muted-foreground/25 hover:border-primary/50'
            }`}
          >
            <input {...getInputProps()} />
            {file ? (
              <div className="flex items-center justify-center gap-3">
                <FileText className="h-10 w-10 text-green-600" />
                <div className="text-left">
                  <p className="font-medium">{file.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {(file.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={(e) => {
                    e.stopPropagation();
                    setFile(null);
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <>
                <Upload className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
                <p className="text-sm text-muted-foreground">
                  {isDragActive
                    ? 'Datei hier ablegen...'
                    : 'Datei hierher ziehen oder klicken zum Auswählen'}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  PDF, Word, Excel, CSV, Bilder (max. 50MB)
                </p>
              </>
            )}
          </div>

          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title">Titel *</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Dokumenttitel eingeben"
            />
          </div>

          {/* Needle Selection */}
          <div className="space-y-2">
            <Label>Nadel-Zuordnung *</Label>
            <Select value={needle} onValueChange={(v) => setNeedle(v as '1' | '2' | '3')}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">
                  <span className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-blue-500" />
                    Nadel 1 - Grundunterlagen
                  </span>
                </SelectItem>
                <SelectItem value="2">
                  <span className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-green-500" />
                    Nadel 2 - Vollstreckungsunterlagen
                  </span>
                </SelectItem>
                <SelectItem value="3">
                  <span className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-orange-500" />
                    Nadel 3 - Sonstige Schriftstücke
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Needle 1: Document Type */}
          {needle === '1' && (
            <div className="space-y-2">
              <Label>Dokumenttyp (Nadel 1) *</Label>
              <Select value={needle1DocType} onValueChange={(v) => setNeedle1DocType(v as Needle1DocumentType)}>
                <SelectTrigger>
                  <SelectValue placeholder="Dokumenttyp auswählen" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(needle1DocTypeLabels).map(([key, label]) => (
                    <SelectItem key={key} value={key}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Needle 2: Case Selection */}
          {needle === '2' && (
            <div className="space-y-2">
              <Label>Haftsache (Nadel 2) *</Label>
              <Select value={caseId} onValueChange={setCaseId}>
                <SelectTrigger>
                  <SelectValue placeholder="Haftsache auswählen" />
                </SelectTrigger>
                <SelectContent>
                  {criminalCases.length === 0 ? (
                    <SelectItem value="" disabled>
                      Keine Haftsachen vorhanden
                    </SelectItem>
                  ) : (
                    criminalCases.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        Haftsache {c.romanNumeral}
                        {c.caseReference && ` - ${c.caseReference}`}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Module Tags */}
          <div className="space-y-2">
            <Label>Module *</Label>
            <div className="flex flex-wrap gap-2">
              {Object.entries(moduleLabels).map(([key, label]) => (
                <Button
                  key={key}
                  type="button"
                  variant={selectedModules.includes(key as ModuleTag) ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => toggleModule(key as ModuleTag)}
                >
                  {label}
                </Button>
              ))}
            </div>
          </div>

          {/* Comment */}
          <div className="space-y-2">
            <Label htmlFor="comment">Sachverfügung / Kommentar</Label>
            <Textarea
              id="comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Optionaler Kommentar zur Sachverfügung"
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={handleClose}>
            Abbrechen
          </Button>
          <Button
            type="button"
            onClick={() => uploadMutation.mutate()}
            disabled={!canSubmit || uploadMutation.isPending}
          >
            {uploadMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Wird hochgeladen...
              </>
            ) : (
              <>
                <Upload className="mr-2 h-4 w-4" />
                Hochladen
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
